import {
  backButton,
  closingBehavior,
  init,
  isTMA,
  mainButton,
  miniApp,
  retrieveLaunchParams,
  retrieveRawInitData,
  secondaryButton,
  swipeBehavior,
  themeParams,
  viewport,
} from '@tma.js/sdk-react'
import { installNativeButtons } from './buttons.tsx'
import { installThemeWatcher } from './theme.ts'

export interface TelegramEnv {
  /** SDK инициализирован (настоящий Telegram или имитация в dev). */
  ready: boolean
  /** Настоящий клиент Telegram: нативные MainButton/BackButton вместо кнопок на странице. */
  native: boolean
  /** Имитация окружения (dev в браузере). */
  mocked: boolean
  initDataRaw: string | null
  startParam: string | null
  platform: string
}

let env: TelegramEnv = {
  ready: false,
  native: false,
  mocked: false,
  initDataRaw: null,
  startParam: null,
  platform: 'unknown',
}

export function getTelegramEnv(): TelegramEnv {
  return env
}

/** Настоящий Telegram: есть WebView-прокси, параметры запуска в hash или мы в iframe веб-клиента. */
function detectRealTelegram(): boolean {
  try {
    if ('TelegramWebviewProxy' in window) return true
    if (window.location.hash.includes('tgWebAppData')) return true
    if (window.self !== window.top) return isTMA()
  } catch {
    return false
  }
  return false
}

function safe(label: string, fn: () => unknown): void {
  try {
    fn()
  } catch (e) {
    if (import.meta.env.DEV) console.warn(`[telegram] ${label}`, e)
  }
}

export async function initTelegram(): Promise<TelegramEnv> {
  const real = detectRealTelegram()
  let mocked = false
  if (!real && import.meta.env.DEV) {
    const { setupMockTelegram } = await import('./mockEnv.ts')
    setupMockTelegram()
    mocked = true
  }
  if (!real && !mocked) {
    installThemeWatcher()
    return env
  }

  try {
    init()
  } catch (e) {
    console.error('[telegram] init failed', e)
    installThemeWatcher()
    return env
  }

  safe('themeParams', () => {
    themeParams.mount.ifAvailable()
    if (!themeParams.isCssVarsBound()) themeParams.bindCssVars.ifAvailable()
  })
  safe('miniApp', () => {
    miniApp.mount.ifAvailable()
    if (!miniApp.isCssVarsBound()) miniApp.bindCssVars.ifAvailable()
    miniApp.setHeaderColor.ifAvailable('secondary_bg_color')
    miniApp.setBgColor.ifAvailable('secondary_bg_color')
    miniApp.setBottomBarColor.ifAvailable('secondary_bg_color')
  })
  safe('backButton', () => backButton.mount.ifAvailable())
  safe('mainButton', () => mainButton.mount.ifAvailable())
  safe('secondaryButton', () => secondaryButton.mount.ifAvailable())
  safe('swipeBehavior', () => {
    swipeBehavior.mount.ifAvailable()
    swipeBehavior.disableVertical.ifAvailable()
  })
  safe('closingBehavior', () => closingBehavior.mount.ifAvailable())
  try {
    if (viewport.mount.isAvailable()) await viewport.mount()
    if (!viewport.isCssVarsBound()) viewport.bindCssVars.ifAvailable()
  } catch (e) {
    if (import.meta.env.DEV) console.warn('[telegram] viewport', e)
  }
  // Без fullscreen: только развернуть на всю высоту
  safe('expand', () => viewport.expand.ifAvailable())

  let startParam: string | null = null
  let platform = 'unknown'
  try {
    const lp = retrieveLaunchParams()
    startParam = lp.tgWebAppStartParam ?? lp.tgWebAppData?.start_param ?? null
    platform = lp.tgWebAppPlatform
  } catch {
    // параметры запуска не читаются — работаем без deep link
  }

  env = {
    ready: true,
    native: real,
    mocked,
    initDataRaw: retrieveRawInitData() ?? null,
    startParam,
    platform,
  }

  installThemeWatcher()
  if (real) installNativeButtons()
  safe('ready', () => miniApp.ready.ifAvailable())
  return env
}
